import React, { useState, useEffect } from 'react';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import PlayerStatPill from './PlayerStatPill';
import palette from '../assets/sass/palette.scss';
import { useUserUtils } from '../utilities/UserUtils';
import { useWaglRating } from '../hooks/useWaglRating';

const GamePlayerInfo = ({
  player,
  index,
  size = { width: '50px', height: '50px', fontSize: '16px' },
  direction = 'column',
  winner = false,
  photoUrl = null,
  onClick = () => {},
  children = null,
}) => {
  const [user, setUser] = useState(null);
  const getUserInitial = (player) => {
    const userName = player?.userName || player?.username || '';
    return userName?.charAt(0)?.toUpperCase();
  };
  const { fetchUserById } = useUserUtils();
  
  // Get on-demand rating for this player
  const playerId = player?.user || player?.uid;
  const { rating: ratingData } = useWaglRating(playerId);


  useEffect(() => {
    const fetchUser = async () => {
      if (!player?.uid && !player?.user) return;
      
      const userId = player.uid || player.user;
      try {
        const userInfo = await fetchUserById(userId);
        if (userInfo) {
          setUser(userInfo);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };
    
    fetchUser();
  }, [player, fetchUserById]);

  const initial = getUserInitial(player);

  return (
    <>
      {direction === 'row' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative' }}>
          <Box sx={{ position: 'relative' }}>
            {photoUrl || player?.photoUrl ? (
              <Avatar
                alt={user?.userName || user?.username || player?.userName || player?.userId}
                sx={{
                  bgcolor: index % 2 === 0 ? '#E9FFEA' : '#F9EDFF',
                  width: size.width,
                  height: size.height,
                }}
                src={user?.photoURL || photoUrl ? photoUrl : player.photoUrl}
              />
            ) : (
              <Box
                sx={{
                  bgcolor: index % 2 === 0 ? '#E9FFEA' : '#F9EDFF',
                  width: size.width,
                  height: size.height,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '50%',
                }}
              >
                <Typography
                  variant="factorA"
                  color="primary"
                  sx={{ fontSize: '24px', color: 'black' }}
                >
                  {initial ? initial : user?.initial || player.initial}
                </Typography>
              </Box>
            )}
            {winner && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -12,
                  right: -12,
                  width: '37.5px',
                  height: '36px',
                  zIndex: 2,
                  pointerEvents: 'none',
                }}
              >
                <svg
                  width="37.5"
                  height="36"
                  viewBox="0 0 25 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="11"
                    transform="matrix(-1 0 0 1 24.9707 0)"
                    fill="#C8B359"
                    stroke="white"
                    strokeWidth="2"
                  />
                  <path
                    d="M8.06967 9.05582H9.04924C9.02203 8.81093 9.02203 8.6522 9.02203 8.62499V8.1488H7.78849C7.58895 8.1488 7.39848 8.23497 7.27149 8.38916C7.13998 8.54789 7.08555 8.74743 7.12184 8.94698C7.27603 9.79957 7.96536 12.3846 10.573 12.8789C10.2601 12.4707 10.0152 12.058 9.81567 11.6544C8.70458 11.0286 8.25107 9.82678 8.06967 9.05582Z"
                    fill="white"
                  />
                  <path
                    d="M18.6398 8.38916C18.5128 8.23497 18.3223 8.1488 18.1228 8.1488H16.8892V8.62499C16.8892 8.65673 16.8892 8.81093 16.862 9.05582H17.8416C17.6647 9.82678 17.2067 11.0331 16.1001 11.6544C15.9006 12.058 15.6557 12.4707 15.3428 12.8789C17.9504 12.3846 18.6398 9.79504 18.794 8.94244C18.8303 8.7429 18.7758 8.53882 18.6443 8.38463L18.6398 8.38916Z"
                    fill="white"
                  />
                  <path d="M15.1934 16.9883H10.7173V17.8953H15.1934V16.9883Z" fill="white" />
                  <path
                    d="M15.979 8.62544V7.81366H9.9292V8.62544C9.9292 8.75242 9.97908 11.6005 12.5097 13.5369V16.0902H13.3985V13.5369C15.9291 11.6005 15.979 8.75242 15.979 8.62544Z"
                    fill="white"
                  />
                </svg>
              </Box>
            )}
          </Box>
          <Stack sx={{ overflow: 'hidden' }}>
            <Typography color="secondary" sx={{ fontWeight: '600' }}>
              {user?.userName || user?.username || player?.userName || player?.userId}
            </Typography>
            <Typography variant="small" component="small">
              Handicap <strong className="color-blue">{player.handicap || 0}</strong>
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Box onClick={() => onClick(index)} sx={{ cursor: 'pointer', textAlign: 'center' }}>
          <Box
            sx={{
              borderRadius: '50%',
              width: size.width,
              height: size.height,
              mx: 'auto',
              mb: 1,
              position: 'relative',
            }}
          >
            {winner && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -12,
                  right: -12,
                  width: '37.5px',
                  height: '36px',
                  zIndex: 2,
                  pointerEvents: 'none',
                }}
              >
                <svg
                  width="37.5"
                  height="36"
                  viewBox="0 0 25 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="11"
                    transform="matrix(-1 0 0 1 24.9707 0)"
                    fill="#C8B359"
                    stroke="white"
                    strokeWidth="2"
                  />
                  <path
                    d="M8.06967 9.05582H9.04924C9.02203 8.81093 9.02203 8.6522 9.02203 8.62499V8.1488H7.78849C7.58895 8.1488 7.39848 8.23497 7.27149 8.38916C7.13998 8.54789 7.08555 8.74743 7.12184 8.94698C7.27603 9.79957 7.96536 12.3846 10.573 12.8789C10.2601 12.4707 10.0152 12.058 9.81567 11.6544C8.70458 11.0286 8.25107 9.82678 8.06967 9.05582Z"
                    fill="white"
                  />
                  <path
                    d="M18.6398 8.38916C18.5128 8.23497 18.3223 8.1488 18.1228 8.1488H16.8892V8.62499C16.8892 8.65673 16.8892 8.81093 16.862 9.05582H17.8416C17.6647 9.82678 17.2067 11.0331 16.1001 11.6544C15.9006 12.058 15.6557 12.4707 15.3428 12.8789C17.9504 12.3846 18.6398 9.79504 18.794 8.94244C18.8303 8.7429 18.7758 8.53882 18.6443 8.38463L18.6398 8.38916Z"
                    fill="white"
                  />
                  <path d="M15.1934 16.9883H10.7173V17.8953H15.1934V16.9883Z" fill="white" />
                  <path
                    d="M15.979 8.62544V7.81366H9.9292V8.62544C9.9292 8.75242 9.97908 11.6005 12.5097 13.5369V16.0902H13.3985V13.5369C15.9291 11.6005 15.979 8.75242 15.979 8.62544Z"
                    fill="white"
                  />
                </svg>
              </Box>
            )}
            {photoUrl || player?.photoURL ? (
              <Avatar
                alt={player.userName || player.userId}
                src={photoUrl ? photoUrl : player.photoUrl}
                sx={{ width: size.width, height: size.height, boxShadow: 2 }}
              />
            ) : (
              <Box
                sx={{
                  width: size.width,
                  height: size.height,
                  borderRadius: '50%',
                  bgcolor: index % 2 === 0 ? palette.paleGreen : palette.palePurple,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="factorA"
                  sx={{ fontSize: '24px', color: '#4C4C4C', fontWeight: 700 }}
                >
                  {initial ? initial : player?.initial}
                </Typography>
              </Box>
            )}
          </Box>
          <Typography
            sx={{
              fontFamily: '"FactorA Medium", arial, sans-serif',
              fontWeight: 700,
              fontSize: size.fontSize,
              textAlign: 'center',
              mb: 0.5,
            }}
          >
            {user?.userName || user?.username || player?.userName || player?.username || player?.userId}
          </Typography>
          <PlayerStatPill
            waglScore={ratingData?.currentRating || 50}
            handicap={user?.handicap || player.handicap || 0}
          />
          {children ?? children}
        </Box>
      )}
    </>
  );
};

export default GamePlayerInfo;
